import Image from "next/image";
import { Fragment } from "react";
import { useInView } from "react-intersection-observer";
import { markdownImageLoader } from "@/lib/image";

/**
 * @link https://tiborsaas.github.io/unique-gradient-generator
 * @link https://angrytools.com/gradient/image
 */
// "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAcAAAADCAYAAABfwxXFAAAAAXNSR0IArs4c6QAAAEtJREFUGFcVyNENgCAMQMFXIDVsRBwABzDsv4TGKNAa/i4nR2k+ZIA7WRSNG1d/WCd1b27BYDjJQIncPulMpJbTwRACjtDfD13OiR+KPx5hQ//1TQAAAABJRU5ErkJggg==";
const GRADIENT_B64 =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABQAAAAUCAYAAACNiR0NAAAAAXNSR0IArs4c6QAAAWBJREFUOE+dlN9nxEAQx3f+oXKEEMJxhOM4Ksf9MX3rU59KKaWUUkqf+tSn/mM9u9TOj53Zvb00aRJWIjvznc98Z+GqvxmdC86F4OIaeJV3H04uXoFX5074b7xxn8/3wWp9iwFD8PQDbqwF5gCSOInghJ7WSsBLSjkgKoyPVa5KodncsUJRQEqldCpVkXhBwt9S6fwdVjFgjIUZbenTTIklKzVModncU1OKjDlTaUpFacEUmuFhzLqWuvqTlXrWfdsUwxTa4ZEYGk55l2tMYy5CVDKFZnhiH3LX2GeCQUuf51Not891Y08yvexTaLcvKaC1gPhMjS7Gn/YpdLvXMRstmZRFTLX70O7eJmcZlS7wKXT7d1RIpZ37Ldllpk+h23/8oZDKUb6SuO5T6K8/i1kujquFTDGgNXU5Efo+z6fQH75w9NTItPG/TCngnAAZx1iTTaynFKyP38XhUBp3GdNf2jxxOJ+YBPgAAAAASUVORK5CYII=";

const getWidthHeightFromParams = (src: string) => {
  const urlObj = new URL(src);
  const w = urlObj.searchParams.get("w");
  const h = urlObj.searchParams.get("h");

  if (w && h && !!parseInt(w) && !!parseInt(h)) {
    return { width: parseInt(w), height: parseInt(h) };
  } else {
    return;
  }
};

const MAX_W_PX = 650;

const CustomImage = ({ src, alt = "", ...imgProps }: React.ComponentPropsWithoutRef<"img">) => {
  if (!src) return null;

  const wh = getWidthHeightFromParams(src);
  if (!wh) return <FallbackImage src={src} alt={alt} {...imgProps} />;

  const layout = wh.width >= MAX_W_PX ? "responsive" : "intrinsic";
  const sizes =
    layout === "responsive" ? `(max-width: ${MAX_W_PX}px) 100vw, ${MAX_W_PX}px` : undefined;
  const priority = new URL(src).searchParams.get("priority") ? true : undefined;

  const Wrapper = layout === "responsive" ? "div" : Fragment;
  const wrapperProps =
    layout === "responsive" ? { style: { display: "block", width: "100%" } } : {};

  return (
    <Wrapper {...wrapperProps}>
      <Image
        alt={alt}
        blurDataURL={GRADIENT_B64}
        layout={layout}
        loader={markdownImageLoader}
        placeholder="blur"
        priority={priority}
        sizes={sizes}
        src={src}
        {...wh}
      />
    </Wrapper>
  );
};

export default CustomImage;

// -----

const FallbackImage = ({ src, alt, ...imgProps }: React.ComponentPropsWithoutRef<"img">) => {
  const { ref, inView } = useInView({ triggerOnce: true });
  const fallbackSrc = GRADIENT_B64;
  return (
    <img
      alt={alt}
      loading="lazy"
      ref={ref}
      src={inView ? src : fallbackSrc}
      style={inView ? undefined : { minWidth: "16rem" }}
      {...imgProps}
    />
  );
};
